import { createSelector } from '@reduxjs/toolkit';
import { Action } from './models';
import { baseApi, fetchbase } from '@centerpiece/components';

export const action = baseApi
  .enhanceEndpoints({ addTagTypes: ['Actions', 'Action'] })
  .injectEndpoints({
    endpoints: builder => ({
      getActions: builder.query<Action[], { params?: object }>({
        query: ({ params }: { params?: object }) => ({
          url: 'action',
          method: 'GET',
          params: params ? { rollup: 'all', ...params } : { rollup: 'all' },
        }),
        providesTags: (result, error, arg) =>
          result
            ? [
                ...result.map(({ id }) => ({ type: 'Action' as const, id })),
                'Actions',
              ]
            : ['Actions'],
      }),
      getAction: builder.query<Action, string>({
        query: id => ({
          url: `action/${id}`,
          method: 'GET',
        }),
        providesTags: (result, error, arg) => [{ type: 'Action', id: arg }],
        // configuration for an individual endpoint, overriding the api setting
        keepUnusedDataFor: 90,
      }),
      createAction: builder.mutation({
        query: request => ({
          url: 'action',
          method: 'POST',
          body: request,
        }),
        invalidatesTags: ['Actions'],
      }),
      updateAction: builder.mutation<Action, Action>({
        query: request => ({
          url: `action/${request.id}`,
          method: 'PUT',
          body: request,
        }),
        invalidatesTags: (result, error, arg) => [
          { type: 'Action', id: arg.id },
        ],
        async onQueryStarted({ id, ...patch }, { dispatch, queryFulfilled }) {
          const patchResult = dispatch(
            action.util.updateQueryData('getAction', `${id}`, draft => {
              Object.assign(draft, patch);
            })
          );
          try {
            await queryFulfilled;
          } catch {
            patchResult.undo();

            /**
             * Alternatively, on failure you can invalidate the corresponding cache tags
             * to trigger a re-fetch:
             * dispatch(action.util.invalidateTags(['Actions']))
             */
            dispatch(action.util.invalidateTags(['Actions']));
          }
        },
      }),
      deleteAction: builder.mutation<Action, number>({
        query: id => ({
          url: `action/${id}`,
          method: 'DELETE',
        }),
        invalidatesTags: (result, error, arg) =>
          result ? [{ type: 'Action' as const, arg }, 'Actions'] : ['Actions'],
      }),
    }),
  });

// Auto-generated hooks
export const {
  useGetActionsQuery,
  useGetActionQuery,
  useCreateActionMutation,
  useUpdateActionMutation,
  useDeleteActionMutation,
  usePrefetch: useActionsPrefetch,
} = action;
const selectActionsRoot = action.endpoints.getActions.select({});
export const selectActions = createSelector(
  selectActionsRoot,
  action => action.data || []
);
